import React, { useEffect, useRef, useState } from 'react';

const STAGE_META = {
  en_servicio: { label: 'En Servicio', color: 'var(--mm-green)' },
  depresurizacion: { label: 'Depresurización', color: 'var(--mm-yellow)' },
  calentamiento: { label: 'Calentamiento', color: 'var(--mm-red)' },
  enfriamiento: { label: 'Enfriamiento', color: 'var(--mm-cyan)' },
  presurizacion: { label: 'Presurización', color: 'var(--mm-yellow)' },
  paralelo: { label: 'Paralelo', color: 'var(--mm-yellow)' },
};

// Order a regenerating dryer moves through before swapping back into service.
const REGEN_ORDER = ['depresurizacion', 'calentamiento', 'enfriamiento', 'presurizacion', 'paralelo'];

export function dryerStageLabel(stage) {
  return STAGE_META[stage]?.label || stage;
}

// Physical dryer identity (A/B) is fixed on screen — only the label under it
// and its color/glow change with status, matching the real plant where the
// vessels themselves never move.
const VESSEL_W = 54;
const VESSEL_TOP = 20;
const VESSEL_BOTTOM = 208;
const TAPER = 26;
const NECK_W = 30;
const GAP = 60;
const LABEL_MARGIN = 156;
const LABEL_OFFSET = 24;
const VESSEL_X = { A: LABEL_MARGIN, B: LABEL_MARGIN + VESSEL_W + GAP };
const CROSSOVER_Y = 170;
const VIEWBOX_W = LABEL_MARGIN * 2 + VESSEL_W * 2 + GAP;
const VIEWBOX_H = 236;

function formatDuration(ms) {
  if (ms == null) return null;
  const totalMin = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h === 0 ? `${m}m` : `${h}h ${m}m`;
}

// Elapsed time in the current stage, excluding whatever's been spent with
// production stopped (including a stopped period still open right now) —
// mirrors manifold-pwa's DryerStatusCard (and FillerView's filling-pause
// math). While `pausedAt` is set the `now` terms cancel out, so the result
// stays constant and the display naturally freezes without special-casing.
export function computeElapsedMs(since, pausedAt, pausedDurationMs, now) {
  if (!since) return 0;
  const start = new Date(since).getTime();
  const openPauseMs = pausedAt ? Math.max(0, now - new Date(pausedAt).getTime()) : 0;
  return Math.max(0, now - start - (pausedDurationMs || 0) - openPauseMs);
}

export function formatElapsedMs(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

// Vessel outline as a tapered hexagon (flat neck, steep trapezoid shoulder,
// straight body) instead of a plain rounded rect — closer to the twin-tower
// heat exchanger reference this was modeled on.
function vesselPath(x) {
  const left = x;
  const right = x + VESSEL_W;
  const neckLeft = x + (VESSEL_W - NECK_W) / 2;
  const neckRight = neckLeft + NECK_W;
  const bodyTop = VESSEL_TOP + TAPER;
  const bodyBottom = VESSEL_BOTTOM - TAPER;
  return [
    `M ${neckLeft} ${VESSEL_TOP}`,
    `L ${neckRight} ${VESSEL_TOP}`,
    `L ${right} ${bodyTop}`,
    `L ${right} ${bodyBottom}`,
    `L ${neckRight} ${VESSEL_BOTTOM}`,
    `L ${neckLeft} ${VESSEL_BOTTOM}`,
    `L ${left} ${bodyBottom}`,
    `L ${left} ${bodyTop}`,
    'Z',
  ].join(' ');
}

// What's actually physically flowing through a vessel at each stage — the
// vessel's "digital rain" now reads this instead of showing arbitrary
// glyphs, and its direction mirrors the real regen gas path: En Servicio
// pulls process air down through the bed; Calentamiento/Enfriamiento push
// hot/cool regen nitrogen counter-current (bottom to top); Paralelo is
// back on air, top to bottom, once the vessel rejoins production.
// Depresurización (a vent, not a through-flow — see VentTube below) and
// Presurización (fed via the crossover tube — see DryerSchematic) are
// deliberately absent here.
const FLOW_META = {
  en_servicio: { symbol: 'AIR', direction: 'down' },
  calentamiento: { symbol: 'N2', direction: 'up' },
  enfriamiento: { symbol: 'N2', direction: 'up' },
  paralelo: { symbol: 'AIR', direction: 'down' },
};

// One column of a repeating gas-symbol "readout" — the caller clips this to
// the vessel silhouette. The glyph leading the direction of travel is
// brightest, the ones trailing behind it fade out, giving a comet shape
// that visibly points the way the gas is moving.
function RainColumn({ x, color, symbol, direction, delay, duration, count = 6 }) {
  const leadIndex = direction === 'up' ? 0 : count - 1;
  return (
    <g
      className={`mm-dryer-rain-col ${direction === 'up' ? 'mm-dryer-rain-col-up' : 'mm-dryer-rain-col-down'}`}
      style={{ animationDelay: `${delay}s`, animationDuration: `${duration}s` }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <text
          key={i}
          x={x}
          y={i * 15}
          textAnchor="middle"
          fontSize="10"
          fontWeight="600"
          fontFamily="'IBM Plex Mono', monospace"
          fill={color}
          fillOpacity={0.14 + (1 - Math.abs(i - leadIndex) / (count - 1)) * 0.8}
        >
          {symbol}
        </text>
      ))}
    </g>
  );
}

// Depresurización is a vent, not a through-flow, so it gets its own icon
// instead of a rain column: a side nozzle blinking while it dumps the
// vessel's residual process air downward before regen gas takes over.
function VentTube({ x, y, outward, color }) {
  const tipX = x + outward * 16;
  return (
    <g className="mm-dryer-vent-blink">
      <line x1={x} y1={y} x2={tipX} y2={y} stroke={color} strokeWidth={2} strokeLinecap="round" />
      <circle cx={tipX} cy={y} r={2.5} fill={color} />
      <line x1={tipX - 3} y1={y + 5} x2={tipX - 5} y2={y + 12} stroke={color} strokeWidth={1.3} strokeLinecap="round" />
      <line x1={tipX} y1={y + 6} x2={tipX} y2={y + 14} stroke={color} strokeWidth={1.3} strokeLinecap="round" />
      <line x1={tipX + 3} y1={y + 5} x2={tipX + 5} y2={y + 12} stroke={color} strokeWidth={1.3} strokeLinecap="round" />
    </g>
  );
}

// The vessel's own outline always stays the app's green (matching the
// theme, like the reference drawing's uniform line work) — status is
// conveyed by a colored glow instead, so "in service" vs. "regenerating"
// reads at a glance without recoloring the structure itself.
function Vessel({ x, label, stage, since, pausedAt, pausedDurationMs, now, side }) {
  const meta = STAGE_META[stage];
  const elapsedMs = computeElapsedMs(since, pausedAt, pausedDurationMs, now);
  const cx = x + VESSEL_W / 2;
  const midY = (VESSEL_TOP + VESSEL_BOTTOM) / 2;
  const labelX = side === 'left' ? x - LABEL_OFFSET : x + VESSEL_W + LABEL_OFFSET;
  const anchor = side === 'left' ? 'end' : 'start';
  const bodyTop = VESSEL_TOP + TAPER;
  const bodyBottom = VESSEL_BOTTOM - TAPER;
  const clipId = `mm-dryer-clip-${label}`;
  const fillId = `mm-dryer-fill-${label}`;
  const flow = FLOW_META[stage];
  const thirdY = bodyTop + (bodyBottom - bodyTop) / 3;
  const twoThirdY = bodyTop + ((bodyBottom - bodyTop) * 2) / 3;

  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <path d={vesselPath(x)} />
        </clipPath>
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={meta.color} stopOpacity="0.06" />
          <stop offset="55%" stopColor={meta.color} stopOpacity="0.20" />
          <stop offset="100%" stopColor={meta.color} stopOpacity="0.32" />
        </linearGradient>
      </defs>

      {/* HUD-style corner brackets around the body — the main "scanner
          frame" cue that reads as futuristic at a glance. */}
      {[
        [x - 5, bodyTop - 5, 1, 1],
        [x + VESSEL_W + 5, bodyTop - 5, -1, 1],
        [x - 5, bodyBottom + 5, 1, -1],
        [x + VESSEL_W + 5, bodyBottom + 5, -1, -1],
      ].map(([bx, by, dx, dy], i) => (
        <path
          key={i}
          d={`M ${bx + dx * 8} ${by} L ${bx} ${by} L ${bx} ${by + dy * 8}`}
          fill="none"
          stroke={meta.color}
          strokeWidth={1.5}
          strokeLinecap="round"
          opacity={0.75}
        />
      ))}

      <path
        className="mm-dryer-vessel-pulse"
        d={vesselPath(x)}
        fill={`url(#${fillId})`}
        stroke="var(--mm-green)"
        strokeWidth={2.5}
        style={{ filter: `drop-shadow(0 0 4px ${meta.color}) drop-shadow(0 0 9px ${meta.color})` }}
      />

      {/* Gas readout + a diagonal glass highlight, both clipped to the
          vessel silhouette so they read as texture inside the tank rather
          than floating decoration. Only rendered for through-flow stages —
          Depresurización (vent) and Presurización (crossover-fed) have no
          internal flow to show. */}
      <g clipPath={`url(#${clipId})`}>
        <path
          d={`M ${x - 8} ${VESSEL_TOP - 8} L ${x + 14} ${VESSEL_TOP - 8} L ${x - 4} ${VESSEL_BOTTOM + 8} L ${x - 26} ${VESSEL_BOTTOM + 8} Z`}
          fill="white"
          fillOpacity={0.05}
        />
        {flow && (
          <>
            <RainColumn x={cx - 11} color={meta.color} symbol={flow.symbol} direction={flow.direction} delay={0} duration={3.6} />
            <RainColumn x={cx + 11} color={meta.color} symbol={flow.symbol} direction={flow.direction} delay={-1.6} duration={4.4} />
          </>
        )}
      </g>

      {/* Depresurización: a blinking side vent instead of a through-flow. */}
      {stage === 'depresurizacion' && (
        <VentTube
          x={side === 'left' ? x : x + VESSEL_W}
          y={bodyTop + 12}
          outward={side === 'left' ? -1 : 1}
          color={meta.color}
        />
      )}

      {/* Body seam lines — layered desiccant-bed texture instead of a
          hollow outline. */}
      <line x1={x + 3} y1={thirdY} x2={x + VESSEL_W - 3} y2={thirdY} stroke="var(--mm-line)" strokeWidth={1} strokeDasharray="2 3" />
      <line x1={x + 3} y1={twoThirdY} x2={x + VESSEL_W - 3} y2={twoThirdY} stroke="var(--mm-line)" strokeWidth={1} strokeDasharray="2 3" />

      {/* Valve-wheel port markers instead of plain dots. */}
      {[VESSEL_TOP, VESSEL_BOTTOM].map((py) => (
        <g key={py}>
          <circle cx={cx} cy={py} r={5} fill="none" stroke={meta.color} strokeWidth={1.2} opacity={0.8} />
          <circle cx={cx} cy={py} r={1.6} fill={meta.color} />
          <line x1={cx - 7} y1={py} x2={cx + 7} y2={py} stroke={meta.color} strokeWidth={1} opacity={0.6} />
          <line x1={cx} y1={py - 7} x2={cx} y2={py + 7} stroke={meta.color} strokeWidth={1} opacity={0.6} />
        </g>
      ))}

      <text x={cx} y={midY + 8} textAnchor="middle" fontSize="24" fontWeight="700" fill="var(--mm-text)">
        {label}
      </text>
      <text x={labelX} y={midY - 9} textAnchor={anchor} fontSize="15" fontWeight="700" fill={meta.color}>
        {meta.label}
      </text>
      <text x={labelX} y={midY + 19} textAnchor={anchor} fontSize="19" fontWeight="700" fill={meta.color} fontFamily="monospace">
        {formatElapsedMs(elapsedMs)}
      </text>
    </g>
  );
}

function DryerSchematic({ infoByDryer, activeDryer, regenStage, now, maxWidth = 380 }) {
  const centerA = VESSEL_X.A + VESSEL_W / 2;
  const centerB = VESSEL_X.B + VESSEL_W / 2;
  const wallA = VESSEL_X.A + VESSEL_W;
  const wallB = VESSEL_X.B;
  const bottomHeaderY = VESSEL_BOTTOM + 12;

  const isParallel = regenStage === 'paralelo';
  const isPresurizing = regenStage === 'presurizacion';
  // Presurización: the active vessel feeds pressurizing air INTO the
  // regenerating one (not yet at pressure). Paralelo: the reverse — the
  // now-ready regenerating vessel opens its outlet and starts joining the
  // active one. The line is drawn A→B, so "forward" means A is the source.
  const crossoverForward = isPresurizing ? activeDryer === 'A' : activeDryer === 'B';
  const crossoverColor = isParallel || isPresurizing ? STAGE_META[regenStage].color : 'var(--mm-line)';
  const crossoverClass = !(isParallel || isPresurizing)
    ? undefined
    : crossoverForward ? 'mm-dryer-flow-line' : 'mm-dryer-flow-line-reverse';

  return (
    <svg viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`} style={{ width: '100%', maxWidth, margin: '0 auto', display: 'block' }}>
      {/* Top inlet header — always static */}
      <line x1={centerA} y1="8" x2={centerA} y2={VESSEL_TOP} stroke="var(--mm-line)" strokeWidth={2} />
      <line x1={centerB} y1="8" x2={centerB} y2={VESSEL_TOP} stroke="var(--mm-line)" strokeWidth={2} />
      <line x1={centerA} y1="8" x2={centerB} y2="8" stroke="var(--mm-line)" strokeWidth={2} />

      {/* Bottom outlet header — always static */}
      <line x1={centerA} y1={VESSEL_BOTTOM} x2={centerA} y2={bottomHeaderY} stroke="var(--mm-line)" strokeWidth={2} />
      <line x1={centerB} y1={VESSEL_BOTTOM} x2={centerB} y2={bottomHeaderY} stroke="var(--mm-line)" strokeWidth={2} />
      <line x1={centerA} y1={bottomHeaderY} x2={centerB} y2={bottomHeaderY} stroke="var(--mm-line)" strokeWidth={2} />

      {/* Crossover tube directly between the two vessels — normally idle,
          and the one that turns into an animated flow line during
          Presurización (active vessel pressurizing the regenerating one)
          and Paralelo (the reverse: the now-ready vessel opening into the
          active one). */}
      <circle cx={wallA} cy={CROSSOVER_Y} r={3} fill="var(--mm-line)" />
      <circle cx={wallB} cy={CROSSOVER_Y} r={3} fill="var(--mm-line)" />
      <line
        x1={wallA} y1={CROSSOVER_Y} x2={wallB} y2={CROSSOVER_Y}
        stroke={crossoverColor}
        strokeWidth={2}
        className={crossoverClass}
      />

      <Vessel
        x={VESSEL_X.A} label="A" stage={infoByDryer.A.stage} since={infoByDryer.A.since}
        pausedAt={infoByDryer.A.pausedAt} pausedDurationMs={infoByDryer.A.pausedDurationMs}
        now={now} side="left"
      />
      <Vessel
        x={VESSEL_X.B} label="B" stage={infoByDryer.B.stage} since={infoByDryer.B.since}
        pausedAt={infoByDryer.B.pausedAt} pausedDurationMs={infoByDryer.B.pausedDurationMs}
        now={now} side="right"
      />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function SetupForm({ initialActiveDryer, initialRegenStage, onSaved, onCancel }) {
  const [activeDryer, setActiveDryer] = useState(initialActiveDryer || 'A');
  const [regenStage, setRegenStage] = useState(initialRegenStage || 'depresurizacion');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch('/api/dryer-events/set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activeDryer, regenStage }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'No se pudo configurar el estado.');
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d-flex flex-column" style={{ gap: 12 }}>
      {error && <div className="alert alert-danger py-2 mb-0">{error}</div>}
      <div className="form-group mb-0">
        <label>Secador En Servicio</label>
        <select className="form-select" value={activeDryer} onChange={(e) => setActiveDryer(e.target.value)}>
          <option value="A">Secador A</option>
          <option value="B">Secador B</option>
        </select>
      </div>
      <div className="form-group mb-0">
        <label>Etapa del Otro Secador</label>
        <select className="form-select" value={regenStage} onChange={(e) => setRegenStage(e.target.value)}>
          {REGEN_ORDER.map((s) => (
            <option key={s} value={s}>{STAGE_META[s].label}</option>
          ))}
        </select>
      </div>
      <div className="d-flex" style={{ gap: 8 }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ flex: 1 }}>
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
        {onCancel && (
          <button className="btn btn-outline-secondary" onClick={onCancel} disabled={saving} style={{ flex: 1 }}>
            Cancelar
          </button>
        )}
      </div>
    </div>
  );
}

export default function DryerStatusPanel({ openSetupSignal, reloadSignal, productionOn, readOnly = false, large = false }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [advancing, setAdvancing] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const panelRef = useRef(null);
  const lastHandledSignal = useRef(openSetupSignal);
  const lastHandledReload = useRef(reloadSignal);

  const load = () => {
    setLoading(true);
    return fetch('/api/dryer-events/current')
      .then((res) => res.json())
      .then((data) => { setState(data); setError(''); })
      .catch((err) => setError(err.message || 'No se pudo cargar el estado de los secadores.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // pausedAt/pausedDurationMs reflect production start/stop history as of
  // the last fetch — since that history can change from outside this panel
  // (the production toggle button above it, or another device entirely),
  // poll periodically so the freeze/resume actually shows up without
  // requiring a manual reload.
  useEffect(() => {
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, []);

  // Lets the production toggle button force an immediate refetch right
  // after it starts/stops production, instead of waiting up to 20s for the
  // next poll — same signal-change pattern as openSetupSignal below.
  useEffect(() => {
    if (reloadSignal === lastHandledReload.current) return;
    lastHandledReload.current = reloadSignal;
    load();
  }, [reloadSignal]);

  useEffect(() => {
    if (!state?.configured) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state?.configured]);

  // Lets the production-start confirmation modal's "Editar Estado de
  // Secadores" option force this panel's setup form open from outside,
  // without imperative refs — every change to the signal opens the form and
  // scrolls it into view, skipping the initial mount.
  useEffect(() => {
    if (openSetupSignal === lastHandledSignal.current) return;
    lastHandledSignal.current = openSetupSignal;
    setSetupOpen(true);
    panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [openSetupSignal]);

  const handleAdvance = async () => {
    setAdvancing(true);
    setError('');
    try {
      const res = await fetch('/api/dryer-events/advance', { method: 'POST' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'No se pudo avanzar la etapa.');
      }
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdvancing(false);
    }
  };

  const body = loading && !state ? (
    <div className="text-muted">Cargando...</div>
  ) : !state?.configured ? (
    readOnly ? (
      <div className="text-muted">El estado de los secadores no ha sido configurado.</div>
    ) : (
      <div className="d-flex flex-column" style={{ gap: 12 }}>
        {error && <div className="alert alert-danger py-2 mb-0">{error}</div>}
        {!setupOpen ? (
          <>
            <div className="text-muted">El estado de los secadores no ha sido configurado.</div>
            <button className="btn btn-primary" style={{ width: 'fit-content' }} onClick={() => setSetupOpen(true)}>
              Configurar Estado
            </button>
          </>
        ) : (
          <SetupForm onSaved={() => { setSetupOpen(false); load(); }} />
        )}
      </div>
    )
  ) : !readOnly && setupOpen ? (
    <SetupForm
      initialActiveDryer={state.active.dryer}
      initialRegenStage={state.regenerating.stage}
      onSaved={() => { setSetupOpen(false); load(); }}
      onCancel={() => setSetupOpen(false)}
    />
  ) : (
    (() => {
      const { active, regenerating } = state;
      const stageIdx = REGEN_ORDER.indexOf(regenerating.stage);
      const isParallel = regenerating.stage === 'paralelo';
      const infoByDryer = {
        A: active.dryer === 'A'
          ? { stage: 'en_servicio', since: active.since, pausedAt: active.pausedAt, pausedDurationMs: active.pausedDurationMs }
          : { stage: regenerating.stage, since: regenerating.since, pausedAt: regenerating.pausedAt, pausedDurationMs: regenerating.pausedDurationMs },
        B: active.dryer === 'B'
          ? { stage: 'en_servicio', since: active.since, pausedAt: active.pausedAt, pausedDurationMs: active.pausedDurationMs }
          : { stage: regenerating.stage, since: regenerating.since, pausedAt: regenerating.pausedAt, pausedDurationMs: regenerating.pausedDurationMs },
      };

      return (
        <div className={`d-flex flex-column ${readOnly ? 'h-100 justify-content-center' : ''}`} style={{ gap: 12 }}>
          {error && <div className="alert alert-danger py-2 mb-0">{error}</div>}

          <DryerSchematic
            infoByDryer={infoByDryer}
            activeDryer={active.dryer}
            regenStage={regenerating.stage}
            now={now}
            maxWidth={large ? 560 : 380}
          />

          <div className="d-flex" style={{ gap: 4 }}>
            {REGEN_ORDER.map((s, i) => (
              <div
                key={s}
                title={STAGE_META[s].label}
                style={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: i <= stageIdx ? STAGE_META[s].color : 'var(--mm-line)',
                }}
              />
            ))}
          </div>

          {!readOnly && (
            <>
              <button className="btn btn-warning" onClick={handleAdvance} disabled={advancing || !productionOn}>
                {advancing
                  ? 'Guardando...'
                  : isParallel ? 'Poner en Servicio →' : 'Avanzar Etapa →'}
              </button>
              {!productionOn && (
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  La producción debe estar en marcha para avanzar la etapa.
                </div>
              )}
            </>
          )}
        </div>
      );
    })()
  );

  // Embedded (readOnly) mode skips the outer card/header entirely — the
  // caller already provides its own card and title, so a second nested
  // card-within-a-card would just duplicate chrome.
  if (readOnly) {
    return (
      <div ref={panelRef} className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
        {state?.configured && state.lastCycleDurationMs != null && (
          <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
            Último ciclo: {formatDuration(state.lastCycleDurationMs)}
          </div>
        )}
        <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>{body}</div>
      </div>
    );
  }

  return (
    <div ref={panelRef} className="card shadow-sm border-0 mb-4">
      <div className="card-header bg-light d-flex justify-content-between align-items-start">
        <div>
          <div className="fw-bold">Estado de Secadores</div>
          {state?.configured && state.lastCycleDurationMs != null && (
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
              Último ciclo: {formatDuration(state.lastCycleDurationMs)}
            </div>
          )}
        </div>
        {state?.configured && (
          <button
            onClick={() => setSetupOpen((v) => !v)}
            title="Configurar secadores"
            className="text-muted"
            style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', lineHeight: 0 }}
          >
            <GearIcon />
          </button>
        )}
      </div>
      <div className="card-body">{body}</div>
    </div>
  );
}
